<template>
  <SearchableSelectComponent
    :options="combinedOptions"
    :modelValue="selectedValue"
    :placeholder="t('go_send.recipient_search_placeholder')"
    :isLoading="isLoading"
    :isError="hasError"
    autoOpen
    @update:modelValue="handleOptionSelected"
    @queryUpdated="handleQueryUpdated"
  >
    <template v-if="isHostedSelected" #trailing>
      <span class="input-group-text">
        <span class="badge bg-primary">{{
          t("hosted_accounts.hosted_account")
        }}</span>
      </span>
    </template>
    <template #description="{ option }">
      <div
        v-if="isHostedOption(option)"
        class="text-muted small text-truncate d-flex align-items-center gap-2"
      >
        <span class="badge bg-primary">{{
          t("hosted_accounts.hosted_account")
        }}</span>
        <span>{{ t("go_send.recipient_hosted_helper") }}</span>
      </div>
      <div
        v-else-if="option.description"
        class="text-muted small text-truncate"
      >
        {{ option.description }}
      </div>
    </template>
  </SearchableSelectComponent>
</template>

<script setup lang="ts">
/**
 * Self-contained recipient picker for the Send GO flow.
 *
 * - Hits TWO endpoints in parallel above the min-query threshold:
 *   * `/api/send-recipients` (hosted-account-enabled companies)
 *   * `/api/utils/companies/` (global producer catalog for EXTERNAL sends)
 * - Hosted rows are rendered first and tagged `[HOSTED]`. External rows
 *   render with country + EECS, matching the legacy dropdown.
 * - Row keys are prefixed (`hosted-` / `external-`) to avoid integer-id
 *   collisions between the two sources.
 * - All picker logic + types live here by design; no separate composable.
 */
import SearchableSelectComponent from "@/components/common/SearchableSelectComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { debounce } from "lodash-es";
import { computed, onBeforeUnmount, ref } from "vue";
import { useI18n } from "vue-i18n";

const MIN_QUERY_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 500;

interface HostedRecipient {
  company_id: number;
  name: string;
  country?: string | null;
}

interface GlobalCompany {
  id: number;
  name: string;
  business_id?: string;
  vat?: string;
  address?: string;
  country: string;
  account_eecs?: string;
}

export interface RecipientSelection {
  recipient_company_id: number | null;
  recipient_company_name: string;
  recipient_eecs_account_number: string;
}

const props = defineProps<{
  modelValue?: string;
  displayValue?: string;
}>();

const emit = defineEmits<{
  select: [value: RecipientSelection];
  "update:modelValue": [value: string];
}>();

const { t } = useI18n();
const { globalErrorHandler } = useGlobalHandlers();

const hostedRecipients = ref<HostedRecipient[]>([]);
const externalCompanies = ref<GlobalCompany[]>([]);
const isLoading = ref(false);
const hasError = ref(false);
const lastSelectedKey = ref<string | undefined>();

const hostedOptions = computed(() =>
  hostedRecipients.value.map((recipient) => ({
    label: recipient.name,
    value: `hosted-${recipient.company_id}`,
    _hostedRecipient: recipient,
  }))
);

const isHostedOption = (option: { value?: string }): boolean =>
  typeof option.value === "string" && option.value.startsWith("hosted-");

const externalOptions = computed(() =>
  externalCompanies.value.map((company) => ({
    label: company.name,
    value: `external-${company.id}`,
    description: [
      company.country,
      company.business_id ? `ID: ${company.business_id}` : null,
      company.account_eecs ? `EECS: ${company.account_eecs}` : null,
    ]
      .filter(Boolean)
      .join(" | "),
    _externalCompany: company,
  }))
);

const customEntryOption = computed(() => {
  const customText = props.displayValue ?? props.modelValue ?? "";
  if (!customText) {
    return null;
  }
  const alreadyMatchedExternal = externalOptions.value.some(
    (option) => option.label === customText
  );
  const alreadyMatchedHosted = hostedOptions.value.some(
    (option) => option.label === customText
  );
  if (alreadyMatchedExternal || alreadyMatchedHosted) {
    return null;
  }
  return {
    label: customText,
    value: "custom_entry",
    description: props.displayValue ? "AI extracted" : "Custom entry",
    _isCustomEntry: true as const,
  };
});

const combinedOptions = computed(() => {
  const options: Array<{ label: string; value: string; description?: string }> =
    [];
  if (customEntryOption.value) {
    options.push(customEntryOption.value);
  }
  options.push(...hostedOptions.value);
  options.push(...externalOptions.value);
  return options;
});

const selectedValue = computed<string | undefined>(() => {
  if (customEntryOption.value) {
    return "custom_entry";
  }
  if (lastSelectedKey.value) {
    const allOptions = combinedOptions.value;
    if (allOptions.some((o) => o.value === lastSelectedKey.value)) {
      return lastSelectedKey.value;
    }
  }
  if (!props.modelValue) {
    return undefined;
  }
  return undefined;
});

const isHostedSelected = computed(
  () => lastSelectedKey.value?.startsWith("hosted-") ?? false
);

const fetchRecipients = async (query: string): Promise<void> => {
  if (query.length < MIN_QUERY_LENGTH) {
    hostedRecipients.value = [];
    externalCompanies.value = [];
    return;
  }
  isLoading.value = true;
  hasError.value = false;
  try {
    const [hostedResponse, externalResponse] = await Promise.all([
      goSolidApi.get("/api/send-recipients", { params: { search: query } }),
      goSolidApi.get("/api/utils/companies/", { params: { search: query } }),
    ]);
    hostedRecipients.value = hostedResponse.data?.recipients ?? [];
    externalCompanies.value = externalResponse.data ?? [];
  } catch (error) {
    globalErrorHandler(error);
    hasError.value = true;
    hostedRecipients.value = [];
    externalCompanies.value = [];
  } finally {
    isLoading.value = false;
  }
};

const debouncedFetchRecipients = debounce(fetchRecipients, SEARCH_DEBOUNCE_MS);

onBeforeUnmount(() => {
  debouncedFetchRecipients.cancel();
});

const handleQueryUpdated = (query: string): void => {
  if (lastSelectedKey.value) {
    lastSelectedKey.value = undefined;
    emit("select", {
      recipient_company_id: null,
      recipient_company_name: query,
      recipient_eecs_account_number: "",
    });
  }
  emit("update:modelValue", query);
  debouncedFetchRecipients(query);
};

const handleOptionSelected = (selectedValue: string): void => {
  if (!selectedValue) {
    return;
  }

  if (selectedValue === "custom_entry") {
    lastSelectedKey.value = undefined;
    const customText = customEntryOption.value?.label ?? "";
    emit("select", {
      recipient_company_id: null,
      recipient_company_name: customText,
      recipient_eecs_account_number: "",
    });
    emit("update:modelValue", customText);
    return;
  }

  lastSelectedKey.value = selectedValue;

  const hostedMatch = hostedOptions.value.find(
    (o) => o.value === selectedValue
  );
  if (hostedMatch) {
    const recipient = hostedMatch._hostedRecipient;
    emit("select", {
      recipient_company_id: recipient.company_id,
      recipient_company_name: recipient.name,
      recipient_eecs_account_number: "",
    });
    emit("update:modelValue", recipient.name);
    return;
  }

  const externalMatch = externalOptions.value.find(
    (o) => o.value === selectedValue
  );
  if (externalMatch) {
    const company = externalMatch._externalCompany;
    emit("select", {
      recipient_company_id: null,
      recipient_company_name: company.name,
      recipient_eecs_account_number: company.account_eecs ?? "",
    });
    emit("update:modelValue", company.name);
  }
};
</script>

<style scoped lang="scss"></style>
